// Per-discipline units on an authorization: the editor on the add/edit modal and
// the compact read-out in the table.
//
// Home-health payers approve visits per discipline — "SN 8, PT 20, OT 12" — and a
// single approved amount cannot say that PT is nearly spent while OT has barely
// started. An authorization with no lines keeps working exactly as before, off
// its one approved amount, so the editor starts empty and adding a line is a
// choice.

import React from "react";

import { DISCIPLINES, LINE_UNITS } from "./api";

let nextKey = 1;
const newKey = () => `line-${nextKey++}`;

// Lines as the form edits them: text inputs, a stable React key.
export function formLinesFrom(lines) {
  return (lines || []).map((l) => ({
    key: newKey(),
    discipline: l.discipline,
    authorized: l.authorized == null ? "" : String(l.authorized),
    unit: l.unit || "visits",
  }));
}

// A whole number, null for blank (no cap), NaN for anything else — refused, not
// coerced, for the same reason as the modal's approved amount: NaN serialises to
// null, which the server reads as "no cap".
function wholeOrNull(text) {
  const t = String(text).trim();
  if (t === "") return null;
  return /^\d+$/.test(t) ? Number(t) : NaN;
}

// The first problem with the lines, or null.
// `blankMeansNoCap` false for approved units, where a blank is refused rather than
// read as no cap, so the message must not offer it.
export function validateLines(formLines, { blankMeansNoCap = true } = {}) {
  const seen = new Set();
  for (const l of formLines) {
    if (!DISCIPLINES.includes(l.discipline)) {
      return "Each line needs a discipline.";
    }
    if (seen.has(l.discipline)) {
      return `${l.discipline} is listed twice — one line per discipline.`;
    }
    seen.add(l.discipline);
    if (Number.isNaN(wholeOrNull(l.authorized))) {
      return blankMeansNoCap
        ? `${l.discipline}: the authorized amount must be a whole number, or blank for no cap.`
        : `${l.discipline}: the approved amount must be a whole number.`;
    }
  }
  return null;
}

// Form lines → the values the API takes. Call only after validateLines.
export function parsedLines(formLines) {
  return formLines.map((l) => ({
    discipline: l.discipline,
    authorized: wholeOrNull(l.authorized),
    unit: l.unit,
  }));
}

export function LinesEditor({
  lines,
  onChange,
  title = "Per-discipline units",
  emptyHint = null,
  showUnit = true,
}) {
  const used = new Set(lines.map((l) => l.discipline));
  const add = () => {
    const free = DISCIPLINES.find((d) => !used.has(d)) || "other";
    onChange([
      ...lines,
      { key: newKey(), discipline: free, authorized: "", unit: "visits" },
    ]);
  };
  const update = (key, field) => (e) =>
    onChange(
      lines.map((l) => (l.key === key ? { ...l, [field]: e.target.value } : l)),
    );
  const remove = (key) => onChange(lines.filter((l) => l.key !== key));

  return (
    <div className="auth-lines-editor">
      <div className="auth-lines-editor-head">
        <span>{title}</span>
        <button
          type="button"
          className="auth-btn auth-btn-link"
          onClick={add}
          disabled={lines.length >= DISCIPLINES.length}
        >
          Add discipline
        </button>
      </div>
      {lines.length === 0 ? (
        <p className="auth-field-hint">
          {emptyHint ||
            "None — the approved amount above covers every discipline. Add lines when the payer approved visits per discipline (SN, PT, OT, ST, MSW, HHA)."}
        </p>
      ) : (
        <table className="auth-lines-table">
          <thead>
            <tr>
              <th>Discipline</th>
              <th>Authorized</th>
              {showUnit && <th>Counted as</th>}
              <th aria-label="Remove" />
            </tr>
          </thead>
          <tbody>
            {lines.map((l) => (
              <tr key={l.key}>
                <td>
                  <select
                    className="auth-input"
                    value={l.discipline}
                    onChange={update(l.key, "discipline")}
                    aria-label="Discipline"
                  >
                    {DISCIPLINES.map((d) => (
                      <option key={d} value={d}>
                        {d}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <input
                    className="auth-input auth-lines-amount"
                    value={l.authorized}
                    onChange={update(l.key, "authorized")}
                    inputMode="numeric"
                    placeholder="no cap"
                    aria-label={`${l.discipline} authorized`}
                  />
                </td>
                {showUnit && (
                  <td>
                    <select
                      className="auth-input"
                      value={l.unit}
                      onChange={update(l.key, "unit")}
                      aria-label={`${l.discipline} counted as`}
                    >
                      {LINE_UNITS.map((u) => (
                        <option key={u} value={u}>
                          {u}
                        </option>
                      ))}
                    </select>
                  </td>
                )}
                <td>
                  <button
                    type="button"
                    className="auth-btn auth-btn-link auth-btn-danger"
                    onClick={() => remove(l.key)}
                    aria-label={`Remove ${l.discipline}`}
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// "PT 18/20 · +3 sched" per line. Uncapped reads "no cap", uncounted reads "—",
// never zero: a line the server did not count is not a line with nothing used.
//
// Since contract 0.3 `remaining` is null on a CAPPED line whenever some of the
// authorization's visits could not be assigned a discipline: `used` is then a
// floor, and a number would read as room left. That renders "— left (some visits
// unassigned)", never "no cap" and never all remaining. `unattributedVisits` is
// the authorization's count, shown once under the lines.
export function LinesUsage({ lines, unattributedVisits = null }) {
  if (!lines || lines.length === 0) return null;
  const unassigned = Number.isFinite(unattributedVisits) && unattributedVisits > 0;
  return (
    <ul className="auth-lines-usage">
      {lines.map((l) => {
        const used = l.used == null ? "—" : l.used;
        const cap = l.authorized == null ? "no cap" : l.authorized;
        // "— left" only where it says something: a counted line whose room is
        // unknown because visits went unassigned. An uncounted line already reads
        // "—/cap".
        const left =
          l.authorized == null
            ? null
            : l.remaining != null
              ? ` · ${l.remaining} left`
              : l.used != null && unassigned
                ? " · — left"
                : null;
        const low =
          l.authorized != null &&
          l.used != null &&
          (l.used >= l.authorized ||
            (l.scheduled != null && l.used + l.scheduled >= l.authorized));
        return (
          <li
            key={l.discipline}
            className={low ? "auth-lines-usage-low" : undefined}
          >
            <strong>{l.discipline}</strong> {used}/{cap}
            {l.unit === "units" ? " units" : ""}
            {left}
            {l.scheduled ? (
              <span className="auth-muted"> · +{l.scheduled} sched</span>
            ) : null}
          </li>
        );
      })}
      {unassigned && (
        <li className="auth-lines-unattributed">
          {unattributedVisits} visit{unattributedVisits === 1 ? "" : "s"}{" "}
          couldn't be assigned a discipline — what is left on each line is
          unknown
        </li>
      )}
    </ul>
  );
}
